import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

import type { LoginPasswordRequest } from '@core/auth/auth.models';
import { FormField } from '@shared/components/form-field/form-field';
import { Spinner } from '@shared/components/spinner/spinner';
import { TextInput } from '@shared/components/text-input/text-input';
import { requiredTrim } from '@shared/validators/required-trim.validator';

/**
 * Username-or-phone + password, the first of the two mandated methods.
 *
 * Owns its own FormGroup and its own submit button rather than borrowing the
 * parent's: the parent switches between two field groups with different
 * validity rules, and a shared control container would need a
 * ControlContainer/viewProviders bridge to gain nothing. The parent keeps what
 * it actually owns - AuthService, navigation and the error text.
 */
@Component({
  selector: 'app-password-login-form',
  imports: [ReactiveFormsModule, FormField, TextInput, Spinner],
  templateUrl: './password-login-form.html',
  styleUrl: './password-login-form.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordLoginForm {
  private readonly fb = inject(NonNullableFormBuilder);

  /** Disables the form and swaps the CTA label while the request is in flight. */
  readonly busy = input(false);

  /** Rendered inline above the CTA. Wrong credentials are never a toast. */
  readonly errorMessage = input('');

  readonly submitted = output<LoginPasswordRequest>();
  readonly forgotPassword = output<void>();

  protected readonly form = this.fb.group({
    userNameOrPhone: this.fb.control('', [requiredTrim()]),
    password: this.fb.control('', [Validators.required]),
    rememberMe: this.fb.control(false),
  });

  /**
   * Errors stay hidden until the first submit, so a half-typed field is not
   * scolded while the user is still typing it.
   */
  private readonly attempted = signal(false);

  protected identifierError(): string {
    const control = this.form.controls.userNameOrPhone;
    if (!this.shouldShow(control.touched)) return '';
    return control.hasError('required') ? 'Enter your email or employee ID.' : '';
  }

  protected passwordError(): string {
    const control = this.form.controls.password;
    if (!this.shouldShow(control.touched)) return '';
    return control.hasError('required') ? 'Enter your password.' : '';
  }

  protected submit(): void {
    this.attempted.set(true);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // getRawValue() is safe here, unlike on the Person form: this group has no
    // conditionally disable()d control, so nothing can be resurrected. It is
    // also necessary - NonNullableFormBuilder types `form.value` as Partial<T>,
    // while getRawValue() returns the complete non-optional shape.
    const value = this.form.getRawValue();
    this.submitted.emit({
      // Trimmed: a trailing space pasted from a spreadsheet is not a different
      // account, and the server would treat it as one.
      userNameOrPhone: value.userNameOrPhone.trim(),
      password: value.password,
      rememberMe: value.rememberMe,
    });
  }

  /** Clears the password only - the identifier is almost certainly correct. */
  resetPassword(): void {
    this.form.controls.password.reset('');
  }

  private shouldShow(touched: boolean): boolean {
    return this.attempted() || touched;
  }
}
